/**
 * `PaidMediaSyncMetaCampaignsButton` — Task 153.
 *
 * Agency-facing manual trigger for IC-5 Meta campaign ingestion on the
 * client paid-media page. Calls the existing
 * `sync-paid-media-meta-campaigns` EF (Task 148) via the authenticated
 * `syncPaidMediaMetaCampaigns` wrapper — normal FE request flow, NEVER
 * service-role.
 *
 * Gating mirrors the EF's server-side authority so the control never
 * renders for an actor the EF would reject:
 *   - `canTriggerPaidMediaSync(me)` — agency-category actor (not sysadmin,
 *     not client-only) holding `paid_media.account.manage`. The EF gate
 *     is the load-bearing authority; this is UX defense-in-depth.
 *   - A syncable target requires an ACTIVE, linked `meta_ads` ad account
 *     whose backing integration_connection is `active` (the EF 404s a
 *     non-active IC, and the token can't decrypt). When the only linked
 *     account needs re-authorization, the control points to re-link
 *     instead of offering an enabled sync.
 *
 * Hard rules (this component MUST NOT):
 *   - Use service-role. All calls go through the authenticated wrapper.
 *   - Auto-sync on mount, poll, or schedule. It fires ONLY on user click
 *     (manual trigger, IC-5 Q3).
 *   - Call Meta directly. The Meta API call lives in the Worker.
 *   - Touch OAuth / token-encryption / publishing surfaces.
 */

import { useCallback, useState } from 'react';
import { isApiError } from '../../api/client';
import { syncPaidMediaMetaCampaigns } from '../../api/endpoints';
import { hasAnyPermission, hasRoleCategory, PERM } from '../../permissions';
import { useMeContext } from '../../context/MeContextProvider';
import type {
  AdAccountRow,
  MeContext,
  PaidMediaSyncSummaryData,
  PaidMediaSyncEntityCounts,
} from '../../api/types';

/**
 * True iff `me` is an agency-category actor (not a sysadmin, not
 * client-only) holding `paid_media.account.manage`. Matches the EF's
 * authorization (`sync-paid-media-meta-campaigns` requires an active
 * agency-category role carrying the permission). Exported for tests.
 */
export function canTriggerPaidMediaSync(me: MeContext | null): boolean {
  if (!me) return false;
  if (me.system_admin) return false;
  if (!hasRoleCategory(me, 'agency')) return false;
  return hasAnyPermission(me.permissions, PERM.PAID_MEDIA_ACCOUNT_MANAGE);
}

/** Shared matcher: an ACTIVE, non-archived, linked `meta_ads` ad account. */
function isActiveLinkedMetaRow(a: AdAccountRow, clientId: string): boolean {
  return (
    a.client_id === clientId &&
    a.platform === 'meta_ads' &&
    a.status === 'active' &&
    a.archived_at === null &&
    typeof a.integration_connection_id === 'string' &&
    a.integration_connection_id.length > 0
  );
}

/**
 * A FE-VISIBLE status meaning the IC is unusable
 * (`reauthorization_required` / `revoked` / `expired`). A missing /
 * undefined / null status is NOT "known non-active": the IC-2 SELECT
 * policy exposes the embedded status only to client users + leadership
 * (`owner` / `marketing_manager`), so a non-leadership agency user with a
 * custom `paid_media.account.manage` grant legitimately sees `null` here.
 */
function isKnownNonActiveIcStatus(status: AdAccountRow['integration_connection_status']): boolean {
  return typeof status === 'string' && status !== 'active';
}

/**
 * The first active, linked `meta_ads` ad account whose backing IC is NOT
 * a KNOWN non-active value — i.e. `active`, OR a status the caller cannot
 * observe (`null` under RLS for a custom-grant agency user, or `undefined`
 * during a wire-version skew). Returns the row (for its `id`) or `null`.
 *
 * Why allow a hidden status (Codex Step A P1): the sync EF authorizes
 * custom agency grants, but those users cannot read the IC status under
 * the IC-2 SELECT policy, so a strict `=== 'active'` gate would hide the
 * button from a user the EF WOULD serve. The EF stays the load-bearing
 * authority — a hidden-but-actually-broken IC surfaces via the 200
 * `status:'failed'` path, which the failure UX maps to "re-link". Only a
 * VISIBLY non-active IC blocks the button up front. Existential over all
 * accounts so a healthy account isn't masked by a sibling. Exported for
 * tests.
 */
export function findSyncableMetaAdAccount(
  accounts: ReadonlyArray<AdAccountRow>,
  clientId: string,
): AdAccountRow | null {
  return (
    accounts.find(
      (a) =>
        isActiveLinkedMetaRow(a, clientId) &&
        !isKnownNonActiveIcStatus(a.integration_connection_status),
    ) ?? null
  );
}

/**
 * True iff an active linked `meta_ads` ad account exists whose backing IC
 * status is a KNOWN (FE-visible) non-active value
 * (`reauthorization_required` / `revoked` / `expired`). Drives the
 * "re-link first" pointer. A missing / undefined / null status does NOT
 * count (hidden, not known-broken — see `findSyncableMetaAdAccount`).
 * Exported for tests.
 */
export function hasReauthRequiredSyncTarget(
  accounts: ReadonlyArray<AdAccountRow>,
  clientId: string,
): boolean {
  return accounts.some(
    (a) =>
      isActiveLinkedMetaRow(a, clientId) &&
      isKnownNonActiveIcStatus(a.integration_connection_status),
  );
}

/**
 * Map a sync failure (wire error code, or the synthetic `sync_failed`
 * for an HTTP-200 `status: 'failed'` summary) to Arabic operator copy.
 * Exported for tests.
 */
export function describeSyncError(code: string): string {
  switch (code) {
    case 'unauthenticated':
      return 'انتهت جلستك. سجّل دخولك من جديد ثم حاول مرة أخرى.';
    case 'permission_denied':
      return 'لا تملك صلاحية مزامنة حملات هذا الحساب.';
    case 'not_found':
      return 'حساب الإعلانات غير متاح للمزامنة.';
    case 'sync_failed':
      return 'تعذّرت مزامنة الحملات. قد يحتاج الحساب إعادة الربط. حاول لاحقاً.';
    case 'internal_error':
      return 'تعذّر تشغيل المزامنة حالياً. حاول مرة أخرى بعد قليل.';
    case 'network_error':
      return 'تعذّر الاتصال بالخدمة. تحقّق من اتصالك بالإنترنت وحاول مرة أخرى.';
    default:
      return 'تعذّرت مزامنة الحملات. حاول مرة أخرى بعد قليل.';
  }
}

/**
 * Summary of a succeeded/partial sync run, reported UP to the parent.
 * The parent owns the durable success banner (Codex Step A P2): this
 * component sits inside the reload-gated subtree and unmounts when the
 * refresh flips the page to `loading`, so a success banner rendered here
 * would be destroyed before the operator could read it.
 */
export interface PaidMediaSyncSummary {
  readonly runStatus: 'succeeded' | 'partial';
  readonly rowsInserted: number;
  readonly rowsUpdated: number;
  // Task 155 — partial = some edge/level was incomplete (data still
  // persisted) — and the per-entity breakdown for the durable banner.
  readonly partial: boolean;
  readonly campaigns: PaidMediaSyncEntityCounts;
  readonly adSets: PaidMediaSyncEntityCounts;
  readonly ads: PaidMediaSyncEntityCounts;
  readonly creatives: PaidMediaSyncEntityCounts;
  readonly dailyInsights: PaidMediaSyncEntityCounts;
}

/**
 * Map the EF sync response → the durable banner summary. Per-entity
 * counts are OPTIONAL on the wire (an older EF/Worker, or a phased-deploy
 * version skew, returns only the base 4 fields), so each defaults to
 * {0,0} and `partial` falls back to the run status. The CAMPAIGNS bucket
 * is special-cased: in the old base shape the aggregate
 * `rows_inserted/rows_updated` ARE the campaign counts (Task 148 only
 * synced campaigns), so we surface those rather than a misleading 0/0.
 * Exported for tests.
 */
export function toSyncSummary(data: PaidMediaSyncSummaryData): PaidMediaSyncSummary {
  const counts = (c?: PaidMediaSyncEntityCounts): PaidMediaSyncEntityCounts => ({
    inserted: c?.inserted ?? 0,
    updated: c?.updated ?? 0,
  });
  return {
    runStatus: data.status === 'partial' ? 'partial' : 'succeeded',
    rowsInserted: data.rows_inserted,
    rowsUpdated: data.rows_updated,
    partial: data.partial ?? data.status === 'partial',
    campaigns: data.campaigns
      ? counts(data.campaigns)
      : { inserted: data.rows_inserted, updated: data.rows_updated },
    adSets: counts(data.ad_sets),
    ads: counts(data.ads),
    creatives: counts(data.creatives),
    dailyInsights: counts(data.daily_insights),
  };
}

type SyncState =
  | { readonly status: 'idle' }
  | { readonly status: 'syncing' }
  | { readonly status: 'failed'; readonly messageAr: string };

interface PaidMediaSyncMetaCampaignsButtonProps {
  readonly clientId: string;
  /** The ad_accounts list for this workspace (from `list-ad-accounts`). */
  readonly accounts?: ReadonlyArray<AdAccountRow>;
  /**
   * Called with the run summary after a succeeded/partial sync. The
   * parent persists the summary (durable banner) AND refreshes the
   * campaign list. A failed run does NOT call this (no refresh; the
   * failure renders inline on this still-mounted component).
   */
  readonly onSyncSucceeded: (summary: PaidMediaSyncSummary) => void;
}

export function PaidMediaSyncMetaCampaignsButton({
  clientId,
  accounts = [],
  onSyncSucceeded,
}: PaidMediaSyncMetaCampaignsButtonProps) {
  const { me } = useMeContext();
  const [sync, setSync] = useState<SyncState>({ status: 'idle' });

  // Derived (plain consts, not hooks) — safe between hook calls.
  const allowed = canTriggerPaidMediaSync(me);
  const syncable = allowed ? findSyncableMetaAdAccount(accounts, clientId) : null;
  const reauthOnly =
    allowed && syncable === null && hasReauthRequiredSyncTarget(accounts, clientId);
  const adAccountId = syncable ? syncable.id : null;

  const handleSync = useCallback(async () => {
    if (!adAccountId) return;
    setSync({ status: 'syncing' });
    try {
      const data = await syncPaidMediaMetaCampaigns({ ad_account_id: adAccountId });
      // A Worker-side failure surfaces as HTTP 200 with status:'failed'
      // (e.g. a token that needs re-authorization). Render it as a
      // failure, NOT success, and do NOT refresh.
      if (data.status === 'failed') {
        setSync({ status: 'failed', messageAr: describeSyncError('sync_failed') });
        return;
      }
      // Report the summary UP (the parent shows the durable banner +
      // refreshes); reset locally since this component is about to unmount
      // on the refresh. toSyncSummary defaults missing per-entity counts.
      onSyncSucceeded(toSyncSummary(data));
      setSync({ status: 'idle' });
    } catch (err: unknown) {
      const code = isApiError(err) ? err.code : 'network_error';
      setSync({ status: 'failed', messageAr: describeSyncError(code) });
    }
  }, [adAccountId, onSyncSucceeded]);

  // Early returns AFTER all hooks (Rules of Hooks — constant hook count).
  if (!allowed) return null;
  if (syncable === null && !reauthOnly) return null;

  if (syncable === null && reauthOnly) {
    return (
      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>مزامنة حملات Meta</h2>
        </div>
        <p className="tiny" style={{ color: 'var(--muted)', marginTop: 6 }}>
          الاتصال بحساب Meta يحتاج إعادة الربط قبل مزامنة الحملات. اطلب من العميل إعادة ربط الحساب،
          ثم أعد المحاولة.
        </p>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>مزامنة حملات Meta</h2>
      </div>
      <p className="tiny" style={{ color: 'var(--muted)', marginTop: 6 }}>
        اسحب أحدث حملات Meta لهذا الحساب الإعلاني يدويًا.
      </p>
      <div style={{ marginTop: 16 }}>
        <button
          type="button"
          className="btn-primary"
          onClick={handleSync}
          disabled={sync.status === 'syncing'}
        >
          {sync.status === 'syncing' ? 'جاري مزامنة الحملات…' : 'مزامنة الحملات'}
        </button>
      </div>
      {sync.status === 'failed' ? (
        <div className="banner banner-error" role="alert" style={{ marginTop: 12 }}>
          {sync.messageAr}
        </div>
      ) : null}
    </div>
  );
}
